import { Checkbox, FormControlLabel } from '@mui/material';
import * as Sentry from '@sentry/react';
import { add, toDate } from "date-fns";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { DISABLE_PAYMENTS_PATH, DISCOUNT_SIBLINGS_PATH, MODEL_COMPLEX_PATH, PACKAGE_ORDERING_CONFIG_PATH, PACKAGES_PATH, studentsPath, userPackagesActive, userPackagesTemp, userProfilePath } from "../../../functions/src/admin/paths";
import { getSchoolYearPackageCatalogStartAt, toDateFromServer } from "../../../functions/src/api/dates";
import { calculatePackagePrices } from "../../../functions/src/common/packagePricing";
import { isRecipientBlockedFromPurchasingCatalog } from "../../../functions/src/common/packagePurchaseEligibility";
import type { Lang as LangText } from "../../../functions/src/types";
import { MealPackage, ModelComplexity, OrderPerson, Student, UserMealPackage, UserProfile } from "../../../functions/src/types";

import { SCHOOL_DEMO } from "../../../functions/src/api/dbConfig";
import type { CrossSchoolSiblingInfo } from "../../../functions/src/common/siblingDiscount";
import { checkCrossSchoolSiblingDiscount } from "../../../functions/src/common/siblingDiscount";
import { auth, currentDb, currentSchoolKey, DataSnapshot, getDatabase } from "../../api/firebase";
import { useDatabase, useDatabaseVal, useList } from "../../api/firebaseHooks";
import { createOrderPaymentWithRetry, placeOrderPackage, type PlaceOrderPackageResult, useBalance, warmPaymentCallables } from "../../api/funds";
import { getLangValue, hasLangFieldContent, langContext, localeFromLang } from "../../api/Lang";
import LoadingSpinner from "../../api/LoadingSpinner";
import OkDlg from "../../api/OkDlg";
import { TopUpProgressBelowLoader } from "../../api/progressBelowLoaders";
import { formatPriceEuro } from "../../api/priceFormat";
import YesNoDlg from "../../api/YesNoDlg";
import { useStrings } from "../../common-strings";
import '../../components.css';

import { demoUsers, NUM_DAYS_SUBSCRIPTION_WARNING, PACKAGE_STATUS_COMPLETED, PACKAGE_STATUS_ORDERED, PACKAGE_STATUS_PAID } from "../../../functions/src/api/constants";
import { isMealPackagePurchasable, isMealPackageVisibleInCatalog, packageCatalogRulesFromModel } from "../../../functions/src/common/packageOrderability";
import { useHasMultipleExchangeableMeals } from "../../api/mealUtils";
import { useTopupStrings } from '../topup-strings';
import PackagePopup, { DISABLE_PAYMENTS_VIVA_DELAYED, DISABLE_PAYMENTS_VIVA_DISABLED } from './PackagePopup';
import PackageWalletCreditsRow, { shouldShowPackageWalletCredits } from './PackageWalletCreditsRow';
import { usePackagesStrings } from './packages-strings';
import './packages.css';
import PackageSubscriptionDetailPopup from './PackageSubscriptionDetailPopup';
import { useServerNowMs } from "../../api/useServerNow";
import { useClearStalePackageTemps } from './useClearStalePackageTemps';

/** Default line for purchased package card when `purchasedInfoText` is unset on the catalog package. */
export const PACKAGE_PURCHASED_INFO_DEFAULT: LangText = {
    el: 'Κυρίως πιάτο, συνοδευτικό ή/και σαλάτα, επιδόρπιο',
    en: 'Main course, side course or/and salad, dessert',
};

// Function to get the current academic year based on the date
// Academic year runs from July 16th to July 15th of the following year
function getAcademicYear(): string {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1; // getMonth() returns 0-11
    const currentDay = now.getDate();

    // If we're between July 16th and December 31st, use current year - next year
    // If we're between January 1st and July 15th, use previous year - current year
    if ((currentMonth === 7 && currentDay >= 16) || currentMonth > 7) {
        return `${currentYear}-${(currentYear + 1).toString().slice(-2)}`;
    } else {
        return `${currentYear - 1}-${currentYear.toString().slice(-2)}`;
    }
}

export default function Packages() {
    const packagesStrings = usePackagesStrings();

    const [modelConstants] = useDatabaseVal<ModelComplexity>(MODEL_COMPLEX_PATH);
    const [packageOrderingConfig] = useDatabaseVal<number>(PACKAGE_ORDERING_CONFIG_PATH);

    const [dlgBuy, setDlgBuy] = useState(null);
    const { uid } = auth.currentUser;
    const students = useDatabase(studentsPath(uid));
    const studentsList: DataSnapshot<OrderPerson>[] = useList(students);
    const numChildren = studentsList?.filter(student => !!student.val()?.student)?.length || 0;
    const hasStaffMembers = studentsList?.some(student => !!student.val()?.personnel);
    const discountSiblings: number = useDatabase(DISCOUNT_SIBLINGS_PATH)?.val() || 0;

    // Check if there are multiple exchangeable meals available using shared utility
    const hasMultipleExchangeableMeals = useHasMultipleExchangeableMeals();

    const userPackages = useDatabase(userPackagesActive(uid) || null);
    const userPackagesTempDb = useDatabase(userPackagesTemp(uid) || null);

    // Get ordered packages from temp packages (where they actually are)
    const userPackagesTempList = useList(userPackagesTempDb).filter(l => l.child('status').val() === PACKAGE_STATUS_ORDERED);

    // Get other packages from active packages
    const paidPackagesList = useList(userPackages).filter(l => l.child('status').val() === PACKAGE_STATUS_PAID);
    const completedPackagesList = useList(userPackages).filter(l => l.child('status').val() === PACKAGE_STATUS_COMPLETED);
    const paidOrCompletedPackagesList = useList(userPackages).filter(l => l.child('status').val() >= PACKAGE_STATUS_PAID);

    // Show awaiting payment form if there are ordered packages in temp packages
    const showPackAwaitingPayment = userPackagesTempList?.length > 0;
    const hasFullPackages = false;
    const numSubscriptions = completedPackagesList?.length;
    const hasSubscriptions = numSubscriptions > 0;

    const [placingOrder, setPlacingOrder] = useState(false);
    const [placingOrderPaymentPending, setPlacingOrderPaymentPending] = useState(false);
    const [staleTempClearedDlg, setStaleTempClearedDlg] = useState(false);
    const serverNowMs = useServerNowMs();
    const onStalePackageTempsCleared = useCallback(() => setStaleTempClearedDlg(true), []);
    useClearStalePackageTemps(userPackagesTempDb, onStalePackageTempsCleared, serverNowMs);

    return <div className='base_margin_setup' style={{ maxWidth: '800px' }}>
        {staleTempClearedDlg && (
            <OkDlg
                title={packagesStrings.awaitingPaymentTitle}
                msg={packagesStrings.stalePackageTempCleared}
                onClose={() => setStaleTempClearedDlg(false)}
            />
        )}
        <div className='center-page'>

            {placingOrder && <LoadingSpinner belowLoader={placingOrderPaymentPending ? <TopUpProgressBelowLoader active /> : undefined} /> ||
                showPackAwaitingPayment &&
                <AwaitingPaymentForm
                    userPackagesList={userPackagesTempList}
                    paidOrCompletedPackagesList={paidOrCompletedPackagesList}
                />
                || <>
                    {dlgBuy &&
                        <PackagePopup packageId={dlgBuy} setDlgBuy={setDlgBuy} setPlacingOrder={setPlacingOrder} setPlacingOrderPaymentPending={setPlacingOrderPaymentPending} />
                    }
                    <div>
                        {modelConstants?.allowPackages && <>


                            <div className='order-heading'>{packagesStrings.packagesHeading(getAcademicYear())}</div>
                            <div className='order-remark' style={{ textAlign: 'justify' }}>{hasStaffMembers ? packagesStrings.packageSubStaff : packagesStrings.packageSub}</div>
                            {hasMultipleExchangeableMeals ? (
                                <div className='order-remark' style={{ marginBottom: hasSubscriptions ? 0 : '40px', textAlign: 'justify' }}>{hasStaffMembers ? packagesStrings.packageChangeStaff : packagesStrings.packageChange}</div>
                            ) : (
                                <div style={{ marginBottom: hasSubscriptions ? 0 : '40px' }}></div>
                            )}

                            {hasSubscriptions && <Subscriptions completedPackagesList={completedPackagesList} />}
                            {!hasFullPackages && <PriceCardsDb setDlgBuy={setDlgBuy} paidOrCompletedPackagesList={paidOrCompletedPackagesList} studentsList={studentsList} packageOrderingConfig={packageOrderingConfig} />}
                            {numChildren > 0 && discountSiblings > 0 &&
                                <div className='multiple-meals-offer-rectangle'>
                                    <div className='ticket-background' style={{ backgroundImage: "url('/img-assets/ticket.svg')" }}>
                                        <div className='ticket-text'>-{discountSiblings}%</div>
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div className='footer-hours-title' style={{ color: '#000000' }}>
                                            {packagesStrings.specialOfferTitle}
                                        </div>
                                        <div className='order-food-remarks-text' style={{ color: '#000000', lineHeight: '16px', marginTop: '10px', width: '100%' }}>
                                            {packagesStrings.specialOfferPrefix}{" "}
                                            <span style={{ fontWeight: 'bold' }}>{discountSiblings}%</span>
                                            {" "}{packagesStrings.specialOfferSuffix}
                                        </div>
                                    </div>
                                </div>
                            }
                        </>}


                    </div>
                    {/* <div className='menu-item-remark' style={{ marginTop: '15px', width: '100%', paddingLeft: '25px' }}><Lang>* Κατεψυγμένο προϊόν|* Frozen product</Lang></div> */}
                </>
            }
        </div>
    </div >
}



function Subscriptions({ completedPackagesList }: SubscriptionsProps) {
    const packagesStrings = usePackagesStrings();

    return <div className='subscriptions-rectangle'>
        <div className='subscriptions-title'>{packagesStrings.activeSubscriptions}</div>
        <SubscriptionCard completedPackagesList={completedPackagesList} />
    </div>
}
type SubscriptionsProps = {
    completedPackagesList: DataSnapshot<UserMealPackage>[];
}
function SubscriptionCard({ completedPackagesList }: SubscriptionCardProps) {

    const commonStrings = useStrings();
    const [lang] = useContext(langContext);
    const { uid } = auth.currentUser;
    const [profile] = useDatabaseVal<UserProfile>(userProfilePath(uid));
    const students = useDatabase(studentsPath(uid));
    const mealPackages =
        useDatabase(PACKAGES_PATH, (ref) => ref.orderByChild('start').startAt(getSchoolYearPackageCatalogStartAt())) || null;
    const mealPackageList = useList(mealPackages);
    const [detailSnap, setDetailSnap] = useState<DataSnapshot<UserMealPackage> | null>(null);

    return <div className="subscription-purchased-card-list">
        {detailSnap && <PackageSubscriptionDetailPopup userPackageSnap={detailSnap} onClose={() => setDetailSnap(null)} />}
        {completedPackagesList.map(ppl => {

            const paidPackage = ppl.val();
            const catalogMeal = mealPackageList.find(
                (p) => p.child('start').val() === paidPackage.start && p.child('end').val() === paidPackage.end
            )?.val() as MealPackage | undefined;
            const purchasedCustom = catalogMeal?.purchasedInfoText?.[lang];
            const purchasedTrimmed = typeof purchasedCustom === 'string' ? purchasedCustom.trim() : '';
            const purchasedLine = purchasedTrimmed || getLangValue(PACKAGE_PURCHASED_INFO_DEFAULT, lang);
            const recipientData = students?.child(paidPackage.recipient).val() as OrderPerson;
            const recipientIsStaff = !!recipientData?.personnel;
            const student: Student = recipientIsStaff ? { 'firstName': profile?.firstName, 'lastName': profile?.lastName, 'classroom': '' } : recipientData as Student;
            const fullName = [student?.firstName, student?.lastName].filter(Boolean).join(' ') || '—';

            const now = toDate(Date.now());
            const endDate = paidPackage.end ? toDateFromServer(paidPackage.end) : null;
            const endMs = endDate ? endDate.getTime() : 0;
            const isActive = !!paidPackage.end && endMs >= Date.now();
            const isEndingSoon =
                isActive &&
                !!endDate &&
                now > add(endDate, { days: -NUM_DAYS_SUBSCRIPTION_WARNING });

            return <div key={ppl.key} className="subscription-purchased-card">
                <div className="subscription-purchased-card-top">
                    <div className="subscription-purchased-card-name">{fullName}</div>
                    <div className="subscription-purchased-card-pack">
                        <div className="subscription-purchased-card-title">{paidPackage.title?.[lang]}</div>
                        <div className="subscription-purchased-card-desc">{purchasedLine}</div>
                    </div>
                    <div
                        className={
                            !isActive
                                ? 'package-subscription-detail-status package-subscription-detail-status-inactive'
                                : isEndingSoon
                                    ? 'package-subscription-detail-status package-subscription-detail-status-ending'
                                    : 'package-subscription-detail-status package-subscription-detail-status-active'
                        }
                    >
                        {!isActive ? (
                            commonStrings.expired
                        ) : isEndingSoon ? (
                            <>
                                <img src="/img-assets/exclamation.svg" alt="" className="subscription-purchased-card-ending-icon" />
                                {commonStrings.endingSoon}
                            </>
                        ) : (
                            <>
                                <img src="/img-assets/check2.svg" alt="" className="package-subscription-detail-check subscription-purchased-card-check" />
                                {commonStrings.active}
                            </>
                        )}
                    </div>
                </div>
                <button
                    type="button"
                    className="subscription-purchased-card-more"
                    onClick={() => setDetailSnap(ppl)}
                >
                    <span>{commonStrings.more}</span>
                    <span className="subscription-purchased-card-chevron" aria-hidden>›</span>
                </button>
            </div>
        })}
    </div>
}

type SubscriptionCardProps = {
    completedPackagesList: DataSnapshot<UserMealPackage>[];
}

function PriceCardsDb({ setDlgBuy, paidOrCompletedPackagesList, studentsList, packageOrderingConfig }: PriceCardsDbProps) {

    const [modelConstants] = useDatabaseVal<ModelComplexity>(MODEL_COMPLEX_PATH);

    const catalogRules = useMemo(() => packageCatalogRulesFromModel(modelConstants), [modelConstants]);

    const priceCards =
        useDatabase(PACKAGES_PATH, (ref) => ref.orderByChild('start').startAt(getSchoolYearPackageCatalogStartAt())) || null;
    const priceCardsList = useList(priceCards);

    // Helper function to check if a package is complete
    const isPackageComplete = (packageSnapshot: DataSnapshot<MealPackage>): boolean => {
        const startVal = packageSnapshot.child('start').val();
        const endVal = packageSnapshot.child('end').val();
        const titleVal = packageSnapshot.child('title').val();
        const priceStudentVal = packageSnapshot.child('priceStudent').val();
        const priceStaffVal = packageSnapshot.child('priceStaff').val();
        const orderVal = packageSnapshot.child('order').val();

        return !!(
            startVal &&
            endVal &&
            hasLangFieldContent(titleVal) &&
            (typeof priceStudentVal === 'number') &&
            (typeof orderVal === 'number')
        );
    };

    return <div className='annual-cards'>
        {priceCardsList
            .filter(packages => isPackageComplete(packages))
            .filter(packages => isMealPackageVisibleInCatalog(
                { start: packages.child('start').val() as string },
                new Date(),
                catalogRules,
            ))
            .sort((a, b) => {
                const orderA = a.child('order').val() || 0;
                const orderB = b.child('order').val() || 0;
                return orderA - orderB;
            })
            .filter(packages => {
                const packageData = packages.val() as MealPackage;
                const catalogStart = packages.child('start').val() as string;
                const catalogEnd = packages.child('end').val() as string;

                const paidRows = paidOrCompletedPackagesList.map((up) => {
                    const v = up.val();
                    return { recipient: v.recipient, start: v.start, end: v.end };
                });

                const recipientsWhoBoughtThis = new Set(
                    paidOrCompletedPackagesList
                        .filter((up) => {
                            const v = up.val();
                            return v.start === catalogStart && v.end === catalogEnd;
                        })
                        .map((up) => up.val().recipient)
                );

                const hasStaffPrice = typeof packageData?.priceStaff === 'number';
                const numStudents = studentsList?.filter((s) => !!s.val()?.student).length || 0;
                const numStaff = studentsList?.filter((s) => !!s.val()?.personnel).length || 0;

                const recipientIds: string[] = [];
                studentsList?.forEach((s) => {
                    const key = s.key;
                    if (!key) return;
                    const isStudent = !!s.val()?.student;
                    const isStaff = !!s.val()?.personnel;
                    if (hasStaffPrice) {
                        if (isStudent || isStaff) recipientIds.push(key);
                    } else if (isStudent) {
                        recipientIds.push(key);
                    }
                });

                if (recipientIds.length === 0) {
                    return true;
                }

                const catalogPick = {
                    start: catalogStart,
                    end: catalogEnd,
                    allowsConcurrentPurchase: packageData?.allowsConcurrentPurchase
                };

                const allBoughtThisRow =
                    (!hasStaffPrice && recipientsWhoBoughtThis.size >= numStudents) ||
                    (hasStaffPrice && recipientsWhoBoughtThis.size >= numStudents + numStaff);

                if (allBoughtThisRow) {
                    return false;
                }

                const allBlocked = recipientIds.every((personId) =>
                    isRecipientBlockedFromPurchasingCatalog(personId, catalogPick, paidRows)
                );

                return !allBlocked;
            })
            .filter(packages => {
                // Hide packages that are only for students when user has no children
                const packageData = packages.val();
                const numChildren = studentsList?.filter(student => !!student.val()?.student)?.length || 0;
                const hasStaffPrice = typeof packageData?.priceStaff === 'number';

                // If user has no children and package has no staff price, hide it
                if (numChildren === 0 && !hasStaffPrice) {
                    return false;
                }

                return true;
            })
            .map(fPackages =>
                <PriceCard
                    key={fPackages.key}
                    id={fPackages.key}
                    mealPackage={fPackages.val()}
                    setDlgBuy={setDlgBuy}
                    studentsList={studentsList}
                    packageOrderingConfig={packageOrderingConfig} />
            )}
    </div>
}
type PriceCardsDbProps = {
    setDlgBuy: (id: string) => void;
    paidOrCompletedPackagesList: DataSnapshot<UserMealPackage>[];
    studentsList: DataSnapshot<OrderPerson>[];
    packageOrderingConfig?: number;
}

function PriceCard({ id, mealPackage, setDlgBuy, studentsList, packageOrderingConfig }: PriceCardProps) {

    const commonStrings = useStrings();
    const [lang] = useContext(langContext);
    const packagesStrings = usePackagesStrings();
    const temp = mealPackage.start === '2525_07_21';
    const numChildren = studentsList?.filter(student => !!student.val()?.student)?.length || 0;
    const hasStaffMembers = studentsList?.some(student => !!student.val()?.personnel);
    const hasBothStudentsAndStaff = numChildren > 0 && hasStaffMembers && typeof mealPackage?.priceStaff === 'number';

    // Check if package start date is more than configured days away
    const now = new Date();
    const packageStartDate = toDateFromServer(mealPackage?.start);
    const daysUntilStart = Math.ceil((packageStartDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    const defaultDays = 10; // fallback value
    const configuredDays = packageOrderingConfig || defaultDays;
    const isPackageTooFarAway = daysUntilStart > configuredDays;

    const onMoreInfoReq = () => {
        setDlgBuy(id);
    }

    return <div style={{ marginBottom: '40px' }}>
        <div className='annual-card-top-rectangle'></div>
        <div className='annual-card-rectangle'>
            <div className='center-page' style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {(isPackageTooFarAway || temp) &&
                    <div className='subscription-card-rectangle' style={{ backgroundColor: '#FFA500', marginTop: '5px' }}>
                        <div className='subscription-card-details' style={{ color: '#FFFFFF', margin: '0', padding: '4px', display: 'flex', justifyContent: 'center' }}>
                            {packagesStrings.availableToOrderInPrefix} {daysUntilStart - configuredDays} {packagesStrings.availableToOrderInSuffix}
                        </div>
                    </div>
                }

                <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <div className='annual-card-heading' style={{ marginTop: (temp || isPackageTooFarAway) ? '10px' : '35px' }}>{mealPackage?.title?.[lang]}</div>
                    <div className='annual-card-price-exp'>
                        {toDateFromServer(mealPackage?.start).toLocaleDateString(localeFromLang(lang))} - {toDateFromServer(mealPackage?.end).toLocaleDateString(localeFromLang(lang))}
                    </div>
                    <div style={{ marginTop: '20px', textAlign: 'center' }}>
                        {hasBothStudentsAndStaff ? (
                            <>
                                <span className='annual-card-price'>{mealPackage?.priceStudent}€</span>
                                <div style={{ color: '#01ABAE', fontSize: '14px', marginTop: '4px' }}>
                                    ({mealPackage?.priceStaff}€ {packagesStrings.personnelPriceSuffix})
                                </div>
                            </>
                        ) : (
                            <span className='annual-card-price'>{numChildren > 0 ? mealPackage?.priceStudent : mealPackage?.priceStaff}€</span>
                        )}
                    </div>
                    {mealPackage?.discount > 0 && <div className='annual-card-offer'>{commonStrings.discount} {mealPackage?.discount}%</div>}
                    <div className='annual-card-heading annual-card-heading2'>{packagesStrings.mealIncludesHeading}</div>
                    <div>
                        <ul className='annual-card-list' style={{ margin: '20px 0 0 0' }}>
                            <li style={{ marginTop: '4px' }}>{packagesStrings.mainCourse}</li>
                            <li style={{ marginTop: '4px' }}>{packagesStrings.saladAndBread}</li>
                            <li style={{ marginTop: '4px' }}>{packagesStrings.fruit}</li>
                        </ul>
                    </div>
                </div>
                <div style={{ marginTop: 'auto', padding: '20px 0 20px 0' }}>
                    <button disabled={temp || isPackageTooFarAway} style={{ fontWeight: 'bold', padding: '14px 15px 13px 12px', width: '100%', marginLeft: '0px' }} className='credit-rectangle-filled' onClick={onMoreInfoReq}>{packagesStrings.moreInfoBuy}</button>
                </div>
            </div>
        </div>
    </div>
}

type PriceCardProps = {
    id: string;
    mealPackage: MealPackage;
    setDlgBuy: (id: string) => void;
    studentsList: DataSnapshot<OrderPerson>[];
    packageOrderingConfig?: number;
}

function AwaitingPaymentForm({ userPackagesList, paidOrCompletedPackagesList }: AwaitingPaymentFormProps) {

    const commonStrings = useStrings();
    const packagesStrings = usePackagesStrings();
    const topupStrings = useTopupStrings();
    const [lang] = useContext(langContext);

    const { uid } = auth.currentUser;
    const serverNowMs = useServerNowMs();
    const [profile] = useDatabaseVal<UserProfile>(userProfilePath(uid));
    const balance = useBalance();
    // Lunchbox credits are applied mandatorily (checkbox checked + disabled)
    const creditsSelected = balance > 0;
    const students = useDatabase(studentsPath(uid));
    const mealPackages = useDatabase(PACKAGES_PATH, (ref) => ref.orderByChild('start').startAt(getSchoolYearPackageCatalogStartAt())) || null;
    const mealPackage: MealPackage = useList(mealPackages)?.filter(sp => sp.key === userPackagesList?.[0]?.child('packageId')?.val())?.[0]?.val();
    const [packageOrderingConfig] = useDatabaseVal<number>(PACKAGE_ORDERING_CONFIG_PATH);
    const [modelConstants] = useDatabaseVal<ModelComplexity>(MODEL_COMPLEX_PATH);
    const catalogRules = useMemo(
        () => packageCatalogRulesFromModel({
            ...modelConstants,
            packageOrderingDays: packageOrderingConfig ?? modelConstants?.packageOrderingDays,
        }),
        [modelConstants, packageOrderingConfig],
    );
    const packagePurchasable = useMemo(
        () => !!(mealPackage && serverNowMs !== undefined && isMealPackagePurchasable(mealPackage, new Date(serverNowMs), catalogRules)),
        [mealPackage, catalogRules, serverNowMs],
    );
    const discountSiblings: number = useDatabase(DISCOUNT_SIBLINGS_PATH)?.val() || 0;

    // Use currentSchoolKey from firebase (same as PackagePopup.tsx)
    // Check for cross-school sibling discount using shared async function
    const [crossSchoolSiblingInfo, setCrossSchoolSiblingInfo] = useState<CrossSchoolSiblingInfo | null>(null);
    const [siblingDiscountVerifyFailed, setSiblingDiscountVerifyFailed] = useState(false);
    useEffect(() => {
        let cancelled = false;
        async function run() {
            if (!mealPackage) {
                setCrossSchoolSiblingInfo(null);
                setSiblingDiscountVerifyFailed(false);
                return;
            }
            try {
                const info = await checkCrossSchoolSiblingDiscount(currentSchoolKey, mealPackage, uid, (url) => getDatabase(url));
                if (!cancelled) {
                    setCrossSchoolSiblingInfo(info);
                    setSiblingDiscountVerifyFailed(false);
                }
            } catch (err) {
                Sentry.captureException(err instanceof Error ? err : new Error(String(err)), {
                    tags: { component: 'AwaitingPaymentForm', operation: 'checkCrossSchoolSiblingDiscount' },
                });
                if (!cancelled) {
                    setCrossSchoolSiblingInfo(null);
                    setSiblingDiscountVerifyFailed(true);
                }
            }
        }
        void run();
        return () => { cancelled = true; };
    }, [currentSchoolKey, mealPackage, uid]);

    const [cancelPackageOrder, setCancelPackageOrder] = useState(false);

    const [dlgDemo, setDlgDemo] = useState(false);
    const [dlgVivaDisabled, setDlgVivaDisabled] = useState(false);
    const [packageActionErrorMsg, setPackageActionErrorMsg] = useState<string | null>(null);
    const [packageInfoMsg, setPackageInfoMsg] = useState<string | null>(null);
    const [disablePayments] = useDatabaseVal<string>(DISABLE_PAYMENTS_PATH);
    const vivaPaymentsDisabled = disablePayments === DISABLE_PAYMENTS_VIVA_DISABLED;
    const vivaPaymentsDelayed = disablePayments === DISABLE_PAYMENTS_VIVA_DELAYED;

    const [creditsUsed, setCreditsUsed] = useState(0);
    const [payAmount, setPayAmount] = useState(0);
    const [loadingSpinner, setLoadingSpinner] = useState(false);
    const [topUpRedirectPending, setTopUpRedirectPending] = useState(false);
    const [ordering, setOrdering] = useState(false);

    const samePackages = paidOrCompletedPackagesList.filter(
        (sp) => sp.child('start').val() === mealPackage?.start && sp.child('end').val() === mealPackage?.end
    );

    // Calculate total price and package prices using shared utility function
    const { totalPrice: calculatedPrice, packagePrices } = useMemo(() => {
        // Convert DataSnapshot objects to plain objects for shared function
        const studentsMap: { [key: string]: OrderPerson } = {};
        if (students) {
            students.forEach((studentSnap) => {
                const studentId = studentSnap.key;
                if (studentId) {
                    studentsMap[studentId] = studentSnap.val() as OrderPerson;
                }
            });
        }

        return calculatePackagePrices({
            userPackagesList: userPackagesList.map(pkg => ({ recipient: pkg.child('recipient').val() })),
            students: studentsMap,
            mealPackage,
            samePackages: samePackages.map(pkg => ({ recipient: pkg.child('recipient').val() })),
            discountSiblings,
            crossSchoolSiblingInfo
        });
    }, [userPackagesList, students, mealPackage, samePackages, discountSiblings, crossSchoolSiblingInfo]);

    // Update state in useEffect (same pattern as PackagePopup.tsx)
    useEffect(() => {
        const calculatedCreditsUsed = calculatedPrice < balance ? -calculatedPrice : -balance;
        const totalPayment = creditsSelected ? calculatedPrice + calculatedCreditsUsed : calculatedPrice;
        setPayAmount(totalPayment);
        setCreditsUsed(calculatedCreditsUsed);
    }, [calculatedPrice, balance, creditsSelected]);

    useEffect(() => {
        if (payAmount > 0) {
            warmPaymentCallables('orderPackage');
        }
    }, [payAmount]);

    // Calculate button text based on payAmount (same logic as PackagePopup.tsx)
    const buttonText = payAmount > 0 ? commonStrings.payPackage : commonStrings.completeOrder;

    const cancelOrder = () => {
        void Promise.all(
            userPackagesList.filter(u => u.child('status').val() < PACKAGE_STATUS_PAID).map(uf => uf.ref.remove())
        ).catch((err) => console.error('Error removing unpaid package rows:', err));
        setCancelPackageOrder(false);
    }

    const onPayPackage = async () => {
        if (serverNowMs === undefined) {
            setPackageActionErrorMsg(packagesStrings.serverTimeNotReady);
            return;
        }
        if (!packagePurchasable) {
            setPackageActionErrorMsg(packagesStrings.pendingPackageNotAvailable);
            return;
        }
        // Block credit card in demo unless user is in demoUsers list (for testing)
        // NOTE: Backend also validates this - comment out backend validation in functions/src/index.ts to allow testing
        const isDemo = currentSchoolKey === SCHOOL_DEMO && !demoUsers.includes(uid);
        if (isDemo && payAmount > 0) {
            setDlgDemo(true);
            return;
        }
        if (vivaPaymentsDisabled === true && payAmount > 0) {
            setDlgVivaDisabled(true);
            return;
        }

        if (payAmount > 0) {
            // Go to bank
            setTopUpRedirectPending(true);
            setLoadingSpinner(true);
            try {
                await createOrderPaymentWithRetry(payAmount, 'orderPackage', {
                    packageId: userPackagesList[0]?.child('packageId').val(),
                });
                setLoadingSpinner(false);
                // Let OrderApp handle payment result
            } catch (error: any) {
                console.error('Failed to redirect to payment:', error);
                setLoadingSpinner(false);

                // Extract error message from Firebase Functions error
                let errorMessage = topupStrings.redirectToPaymentFailed;
                if (error?.code === 'functions/failed-precondition' || error?.code === 'failed-precondition') {
                    errorMessage = error.message || topupStrings.topUpNotAvailableInDemo;
                } else if (error?.code === 'functions/internal' || error?.code === 'internal') {
                    errorMessage = topupStrings.redirectToPaymentFailed;
                } else if (error?.message) {
                    errorMessage = error.message;
                } else if (typeof error === 'string') {
                    errorMessage = error;
                }

                setPackageActionErrorMsg(errorMessage);
            } finally {
                setTopUpRedirectPending(false);
            }
        } else {
            onCompleteOrder();
        }
    }

    const onCompleteOrder = async () => {

        if (ordering)
            return;

        if (serverNowMs === undefined) {
            setPackageActionErrorMsg(packagesStrings.serverTimeNotReady);
            return;
        }

        setLoadingSpinner(true);
        setOrdering(true);

        try {
            const userDate = serverNowMs;
            const price = -creditsUsed;
            const result = await placeOrderPackage({ packageId: userPackagesList[0]?.child('packageId').val(), userDate, price, localDbURL: currentDb.databaseURL });
            onOrderCompleted(result);
        } catch (error) {
            Sentry.captureException(error);
            onOrderCompleted({ error: (error instanceof Error ? error.message : "") || topupStrings.packageOrderCompletionFailed, });
        } finally {
            setOrdering(false);
            setLoadingSpinner(false);
        }
    }

    function onOrderCompleted(result: PlaceOrderPackageResult) {
        if (result.error) {
            setPackageActionErrorMsg(result.error);
            return;
        }
        if (result.copyComplete === false) {
            setPackageInfoMsg(result.message || packagesStrings.packageCopyFinishingByUs);
        }
    }

    return <div>
        {loadingSpinner && <LoadingSpinner belowLoader={topUpRedirectPending ? <TopUpProgressBelowLoader active /> : undefined} />}
        {dlgDemo && <OkDlg title={packagesStrings.demoNoCreditCardTitle} msg={packagesStrings.demoNoCreditCardMsg} onClose={() => setDlgDemo(false)} />}
        {dlgVivaDisabled && <OkDlg title={topupStrings.paymentPausedDlgTitle} msg={topupStrings.vivaDisabledLongBody} onClose={() => setDlgVivaDisabled(false)} />}
        {packageInfoMsg !== null && (
            <OkDlg title={packagesStrings.packageRegisteredDlgTitle} msg={packageInfoMsg} onClose={() => setPackageInfoMsg(null)} />
        )}
        {packageActionErrorMsg !== null && (
            <OkDlg title={topupStrings.paymentErrorTitle} msg={packageActionErrorMsg} onClose={() => setPackageActionErrorMsg(null)} />
        )}
        {cancelPackageOrder &&
            <YesNoDlg title={commonStrings.cancelOrder} msg={packagesStrings.cancelOrderSubtitle} yes={commonStrings.cancelConfirm} no={commonStrings.no} onNo={() => setCancelPackageOrder(false)} onYes={cancelOrder} />
        }
        <div className='await-pay-title'>{packagesStrings.awaitingPaymentTitle}</div>
        <div className="payment-alert-rectangle">
            <img src='/img-assets/alert2.svg' className="payment-alert-img"></img>
            <div className='await-pay-subtitle'>{packagesStrings.awaitingPaymentSubtitle}</div>
        </div>
        {siblingDiscountVerifyFailed && (
            <div className="payment-alert-rectangle" style={{ marginTop: '16px' }}>
                <img src='/img-assets/alert2.svg' className="payment-alert-img" alt="" />
                <div className='await-pay-subtitle'>
                    <strong>{packagesStrings.siblingDiscountVerifyFailedTitle}</strong>
                    <div style={{ marginTop: '8px' }}>{packagesStrings.siblingDiscountVerifyFailedMsg}</div>
                </div>
            </div>
        )}
        <div className='step-terms' style={{ letterSpacing: '0.32px', marginTop: '36px', fontWeight: 500 }}>{mealPackage?.title?.[lang]}</div>
        <div className='offer-rectangle'>
            {(() => {
                // Early return if no students - before any hooks are called
                if (!students) {
                    return [];
                }

                let arr = [];
                for (let i = 0; i < packagePrices.length; i++) {
                    const packagePriceData = packagePrices[i];
                    const userPackage = userPackagesList[i];
                    const recipientId = userPackage.child('recipient').val();
                    const recipientData = students?.child(recipientId).val() as OrderPerson;
                    const student: Student = packagePriceData.recipientIsStaff ? { 'firstName': profile?.firstName, 'lastName': profile?.lastName, 'classroom': '' } : recipientData as Student;

                    arr.push(
                        <div className='offer-row' key={student.firstName || 'staff'}>
                            <div>{student.firstName} {student.lastName}</div>
                            <div style={{ color: '#3C3C3C' }}>
                                {packagePriceData.isSibling && <span className='offer-discounted-price'>{mealPackage?.priceStudent}€</span>}
                                {typeof packagePriceData.price === "number" ? formatPriceEuro(packagePriceData.price, lang) : ""}
                            </div>
                        </div>
                    );
                    // if (packagePrices.length > i + 1)
                    arr.push(<div className='offer-price-separator' key={'Middle' + i + 'Sep'} />);
                }
                // Use calculatedPrice from useMemo for total
                const price = calculatedPrice;
                const showWalletCredits = shouldShowPackageWalletCredits(balance ?? 0, price);

                if (showWalletCredits) {
                    const balanceAfterRemain = price < balance ? balance - price : 0;
                    arr.push(
                        <PackageWalletCreditsRow
                            key="useWallet"
                            label={packagesStrings.walletLabel}
                            creditsUsed={creditsUsed}
                            balanceAfterRemain={balanceAfterRemain}
                            remainLabel={packagesStrings.willRemainPrefix}
                        />
                    );
                }
                arr.push(<div className='offer-price-total-separator' key={'TotalSep'} />);


                arr.push(
                    <div className='offer-row' style={{ justifyContent: 'right' }} key={'Total'}>
                        {packagesStrings.totalLabel}
                        <span style={{ fontWeight: 'bold', marginLeft: '12px' }}>{formatPriceEuro(Number(payAmount), lang)}</span>
                    </div>
                )
                return arr;
            })()}
        </div>
        {!packagePurchasable && (
            <div className="payment-alert-rectangle" style={{ marginTop: '16px' }}>
                <img src='/img-assets/alert2.svg' className="payment-alert-img" alt="" />
                <div className='await-pay-subtitle'>{packagesStrings.pendingPackageNotAvailable}</div>
            </div>
        )}
        <button
            type='button'
            className="payment-button"
            onClick={() => onPayPackage()}
            disabled={!packagePurchasable || (vivaPaymentsDisabled === true && payAmount > 0)}
        >
            {buttonText}
        </button>
        {vivaPaymentsDisabled === true && payAmount > 0 && (
            <div className="payment-warning">
                <div style={{ fontWeight: 700, marginBottom: '6px' }}>
                    {packagesStrings.paymentPauseTitle}
                </div>
                {topupStrings.vivaDisabledLongBody}
            </div>
        )}
        {vivaPaymentsDelayed === true && payAmount > 0 && (
            <div className="payment-warning">
                <div style={{ fontWeight: 700, marginBottom: '6px' }}>
                    {packagesStrings.walletDelayTitle}
                </div>
                {topupStrings.vivaDelayedLongBody}
            </div>
        )}
        <div className='package-bank'>
            {packagesStrings.needHelpPrefix}{" "}
            <div style={{ fontWeight: 500, marginTop: '5px' }}><a href="mailto:info@lunch-box.gr">info@lunch-box.gr</a></div>
            <div className='await-pay-cancel'>
                <span style={{ cursor: 'pointer' }} onClick={() => setCancelPackageOrder(true)}>{commonStrings.cancelOrder}</span>
            </div>
        </div>
    </div >
}
type AwaitingPaymentFormProps = {
    userPackagesList: DataSnapshot[];
    /** Paid or completed active packages (sibling discount must see completed, same as PackagePopup step 3). */
    paidOrCompletedPackagesList: DataSnapshot[];
}